import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { TrendingUp, Sun, Wind, Info } from 'lucide-react';
import '../components/AdvancedDashboard.css';
import './pages.css';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import { useAuth } from '../auth';
import { LoadingState, EmptyState, ErrorState } from '../components/ui/States';
import { fetchProductionForecast, fetchProductionForecastComparison } from '../historyApi';

const RANGES = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'tomorrow', label: 'Demain' },
  { id: '7d', label: '7 jours' }
];

function localHour(isoLike) {
  return typeof isoLike === 'string' && isoLike.length >= 16 ? isoLike.slice(11, 16) : '--:--';
}
function localDateLabel(dateStr) {
  if (typeof dateStr !== 'string') return dateStr;
  const [, m, d] = dateStr.split('-');
  return `${d}/${m}`;
}
function dayNameLabel(dateStr, index) {
  if (index === 0) return "Aujourd'hui";
  if (index === 1) return 'Demain';
  try {
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long' });
  } catch {
    return dateStr;
  }
}

export default function ProductionForecastPage() {
  const { plants } = useOutletContext();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const safePlants = Array.isArray(plants) ? plants : [];

  const [plantId, setPlantId] = useState('');
  const [range, setRange] = useState('today');
  const [forecast, setForecast] = useState(null);
  const [forecastStatus, setForecastStatus] = useState('idle');
  const [comparison, setComparison] = useState(null);
  const [comparisonStatus, setComparisonStatus] = useState('idle');

  useEffect(() => {
    if (!plantId && safePlants.length > 0) setPlantId(safePlants[0].plant_id);
  }, [safePlants, plantId]);

  const loadForecast = useCallback(() => {
    if (!plantId) return;
    setForecastStatus('loading');
    fetchProductionForecast(token, plantId)
      .then((res) => { setForecast(res); setForecastStatus('ok'); })
      .catch(() => { setForecast(null); setForecastStatus('error'); });
  }, [token, plantId]);

  const loadComparison = useCallback(() => {
    if (!plantId) return;
    setComparisonStatus('loading');
    fetchProductionForecastComparison(token, plantId, 14)
      .then((res) => { setComparison(res); setComparisonStatus('ok'); })
      .catch(() => { setComparison(null); setComparisonStatus('error'); });
  }, [token, plantId]);

  useEffect(() => {
    setForecast(null); setForecastStatus('idle');
    setComparison(null); setComparisonStatus('idle');
  }, [plantId]);

  useEffect(() => { if (plantId) loadForecast(); }, [plantId, loadForecast]);
  useEffect(() => { if (plantId) loadComparison(); }, [plantId, loadComparison]);

  const dayIndex = range === 'tomorrow' ? 1 : range === 'today' ? 0 : null;
  const selectedDay = (dayIndex !== null && forecast?.daily) ? forecast.daily[dayIndex] : null;

  const weekEnergyKwh = useMemo(() => {
    if (!forecast?.daily) return null;
    return forecast.daily.reduce((s, d) => s + (d.predicted_energy_kwh || 0), 0);
  }, [forecast]);
  const weekPeak = useMemo(() => {
    if (!forecast?.daily) return null;
    let peak = null;
    forecast.daily.forEach((d) => {
      if (!peak || d.peak_power_kw > peak.peak_power_kw) peak = d;
    });
    return peak;
  }, [forecast]);

  const chartData = useMemo(() => {
    if (!comparison?.comparisons) return [];
    return comparison.comparisons.map((c) => ({
      date: localDateLabel(c.date),
      'Production prévue': c.predicted_energy_kwh,
      'Production réelle': c.actual_energy_kwh
    }));
  }, [comparison]);

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Tableau de bord', to: '/' }, { label: 'Prévision de Production' }]} />
      <div className="page-header">
        <h1><TrendingUp size={22} /> Prévision de Production</h1>
      </div>

      {safePlants.length === 0 ? (
        <EmptyState label="Aucune centrale accessible pour votre compte" />
      ) : (
        <>
          <div className="summary-card" style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
            <div className="form-group" style={{ marginBottom: 0, minWidth: 200 }}>
              <label>Centrale</label>
              <select value={plantId} onChange={(e) => setPlantId(e.target.value)}>
                {safePlants.map((p) => <option key={p.plant_id} value={p.plant_id}>{p.name || p.plant_id}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Période</label>
              <div className="time-range-selector">
                {RANGES.map((r) => (
                  <button key={r.id} className={range === r.id ? 'active' : ''} onClick={() => setRange(r.id)}>{r.label}</button>
                ))}
              </div>
            </div>
          </div>

          {forecastStatus === 'loading' && <LoadingState label="Calcul de la prévision de production..." />}
          {forecastStatus === 'error' && <ErrorState label="Impossible de calculer la prévision de production" />}

          {forecastStatus === 'ok' && forecast?.forecast_available === false && forecast.reason === 'config_incomplete' && (
            <div className="section">
              <EmptyState label="Prévision de production indisponible" />
              <div className="summary-card" style={{ marginTop: 12 }}>
                <div className="summary-label" style={{ marginBottom: 8 }}>Configuration à compléter</div>
                <ul style={{ margin: 0, paddingLeft: 20, color: '#ccc' }}>
                  {(forecast.missing || []).map((m) => <li key={m.key}>{m.label}</li>)}
                </ul>
                {user?.role === 'admin' ? (
                  <button className="btn-secondary" style={{ marginTop: 14 }} onClick={() => navigate('/admin/plants')}>Aller à Gestion des Centrales</button>
                ) : (
                  <div className="summary-sub" style={{ marginTop: 10 }}>
                    Contactez un administrateur pour compléter la configuration de cette centrale.
                  </div>
                )}
              </div>
            </div>
          )}
          {forecastStatus === 'ok' && forecast?.forecast_available === false && forecast.reason === 'no_coordinates' && (
            <EmptyState label="Coordonnées de la centrale non configurées - impossible de récupérer les prévisions météo" />
          )}
          {forecastStatus === 'ok' && forecast?.forecast_available === false && forecast.reason === 'provider_error' && (
            <ErrorState label="Prévisions météo temporairement indisponibles" />
          )}

          {forecastStatus === 'ok' && forecast?.forecast_available && (
            <>
              {/* SUMMARY */}
              <div className="section">
                <h3 className="section-title">Résumé</h3>
                <div className="summary-grid summary-grid-compact">
                  <div className="summary-card">
                    <div className="summary-label">
                      {range === '7d' ? 'Production prévue (7 jours)' : `Production prévue (${dayNameLabel(selectedDay?.date, dayIndex).toLowerCase()})`}
                    </div>
                    <div className="summary-value">
                      {(range === '7d' ? weekEnergyKwh : selectedDay?.predicted_energy_kwh)?.toFixed(1) ?? '—'}
                      <span className="summary-unit"> kWh</span>
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Puissance maximale prévue</div>
                    <div className="summary-value">
                      {(range === '7d' ? weekPeak?.peak_power_kw : selectedDay?.peak_power_kw)?.toFixed(2) ?? '—'}
                      <span className="summary-unit"> kW</span>
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Pic de production prévu</div>
                    <div className="summary-value" style={{ fontSize: '1.2rem' }}>
                      {range === '7d'
                        ? (weekPeak ? `${localDateLabel(weekPeak.date)} ${weekPeak.peak_time || ''}` : '—')
                        : (selectedDay?.peak_time || '—')}
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Puissance installée</div>
                    <div className="summary-value">{forecast.installed_capacity_kw?.toFixed(2)}<span className="summary-unit"> kWp</span></div>
                  </div>
                </div>
              </div>

              {/* HOURLY (today/tomorrow only) */}
              {selectedDay && (
                <div className="section">
                  <h3 className="section-title">Prévision Horaire — {dayNameLabel(selectedDay.date, dayIndex)}</h3>
                  <div className="hourly-forecast-grid">
                    {selectedDay.hourly.map((h) => (
                      <div key={h.time} className="hourly-forecast-card">
                        <div className="hourly-forecast-time">{localHour(h.time)}</div>
                        <div className="hourly-forecast-temp">
                          {h.predicted_power_kw !== null ? h.predicted_power_kw.toFixed(2) : '—'} kW
                        </div>
                        <div className="hourly-forecast-row">
                          {h.predicted_energy_kwh !== null ? h.predicted_energy_kwh.toFixed(2) : '—'} kWh
                        </div>
                        <div className="hourly-forecast-row"><Sun size={11} />{h.irradiance_w_m2 ?? '—'} W/m²</div>
                        <div className="hourly-forecast-row">🌡️ {h.temperature_c !== null ? h.temperature_c.toFixed(1) : '—'}°C</div>
                        <div className="hourly-forecast-row">☁️ {h.cloud_cover_pct ?? '—'}%</div>
                        <div className="hourly-forecast-row"><Wind size={11} />{h.wind_ms !== null ? h.wind_ms.toFixed(1) : '—'} m/s</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* DAILY */}
              <div className="section">
                <h3 className="section-title">Prévision sur 7 Jours</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead><tr><th>Jour</th><th>Date</th><th>Énergie prévue</th><th>Puissance max</th><th>Pic</th></tr></thead>
                    <tbody>
                      {forecast.daily.map((d, i) => (
                        <tr key={d.date}>
                          <td style={{ textTransform: 'capitalize' }}>{dayNameLabel(d.date, i)}</td>
                          <td>{localDateLabel(d.date)}</td>
                          <td>{d.predicted_energy_kwh.toFixed(1)} kWh</td>
                          <td>{d.peak_power_kw.toFixed(2)} kW</td>
                          <td>{d.peak_time || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* FORECAST VS ACTUAL */}
              <div className="section">
                <h3 className="section-title">Prévision vs Réel</h3>
                {comparisonStatus === 'loading' && <LoadingState label="Chargement de la comparaison..." />}
                {comparisonStatus === 'error' && <ErrorState label="Impossible de charger la comparaison prévision/réel" />}
                {comparisonStatus === 'ok' && (!comparison?.comparisons || comparison.comparisons.length === 0) && (
                  <EmptyState label="Aucune période passée disponible pour la comparaison pour le moment" />
                )}
                {comparisonStatus === 'ok' && comparison?.comparisons?.length > 0 && (
                  <>
                    <div className="chart-panel" style={{ marginBottom: 16 }}>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#444" />
                          <XAxis dataKey="date" stroke="#888" tick={{ fill: '#888' }} />
                          <YAxis stroke="#888" tick={{ fill: '#888' }} label={{ value: 'kWh', angle: -90, position: 'insideLeft', fill: '#888' }} />
                          <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', borderRadius: '8px' }} />
                          <Legend />
                          <Bar dataKey="Production prévue" fill="#7db3ff" />
                          <Bar dataKey="Production réelle" fill="#6fd99a" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table">
                        <thead>
                          <tr><th>Date</th><th>Prévue</th><th>Réelle</th><th>Écart</th><th>Erreur %</th><th>Prévision utilisée</th></tr>
                        </thead>
                        <tbody>
                          {comparison.comparisons.map((c) => (
                            <tr key={c.date}>
                              <td>{localDateLabel(c.date)}</td>
                              <td>{c.predicted_energy_kwh.toFixed(1)} kWh</td>
                              <td>{c.actual_energy_kwh !== null ? `${c.actual_energy_kwh.toFixed(1)} kWh` : 'Non disponible'}</td>
                              <td>{c.energy_difference_kwh !== null ? `${c.energy_difference_kwh > 0 ? '+' : ''}${c.energy_difference_kwh.toFixed(1)} kWh` : '—'}</td>
                              <td>{c.energy_error_pct !== null ? `${c.energy_error_pct > 0 ? '+' : ''}${c.energy_error_pct}%` : 'N/A'}</td>
                              <td title={`Générée ${new Date(c.primary_generated_at).toLocaleString('fr-FR')}`}>J-{c.primary_lead_days}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              {/* METHOD EXPLANATION */}
              <div className="section">
                <details>
                  <summary className="section-title" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Info size={16} /> Méthode de calcul
                  </summary>
                  <div className="summary-sub" style={{ marginTop: 10, lineHeight: 1.6, maxWidth: 720 }}>
                    Il s'agit d'une <strong>estimation de production</strong>, pas d'une valeur garantie. Le calcul combine :
                    la puissance installée de la centrale, les prévisions météorologiques Open-Meteo (rayonnement solaire sur le
                    plan des panneaux, température, vent), l'orientation et l'inclinaison des panneaux, les pertes système
                    configurées (câblage, salissure, discordance) et le rendement de l'onduleur. Cette estimation ne
                    remplace pas une mesure réelle - consultez la section "Prévision vs Réel" ci-dessus pour comparer avec
                    la production effectivement mesurée par les panneaux une fois la période passée.
                  </div>
                </details>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
